import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { elsterApi, ApiError, type ElsterLicense } from '../../../lib/api';
import { Button } from '../../ui';

// ---- Types ----------------------------------------------------------------

interface LicenseState {
  accepted: boolean;
  loading: boolean;
  license: ElsterLicense | null;
}

interface UseLicenseReturn {
  accepted: boolean;
  loading: boolean;
  /** Call before any ELSTER action; resolves true when accepted, false on cancel */
  ensureAccepted: () => Promise<boolean>;
  /** Renders the modal when open — include this in your JSX tree */
  Modal: ReactElement | null;
}

// ---- Hook -----------------------------------------------------------------

/**
 * useElsterLicense(companyId)
 *
 * Returns { accepted, loading, ensureAccepted, Modal }.
 *
 * Usage:
 *   const { ensureAccepted, Modal } = useElsterLicense(companyId);
 *   // in JSX: {Modal}
 *   // before action: if (!(await ensureAccepted())) return;
 */
export function useElsterLicense(companyId: string): UseLicenseReturn {
  const [state, setState] = useState<LicenseState>({ accepted: false, loading: true, license: null });
  const [modalOpen, setModalOpen] = useState(false);
  // Resolve the pending promise when the user accepts or cancels.
  const resolveRef = useRef<((v: boolean) => void) | null>(null);
  const [checks, setChecks] = useState<boolean[]>([]);

  useEffect(() => {
    let cancelled = false;
    setState({ accepted: false, loading: true, license: null });
    elsterApi
      .license(companyId)
      .then((res) => {
        if (cancelled) return;
        setState({ accepted: res.accepted, loading: false, license: res.license });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ accepted: false, loading: false, license: null });
      });
    return () => { cancelled = true; };
  }, [companyId]);

  const openModal = useCallback((license: ElsterLicense): Promise<boolean> => {
    setChecks(license.confirmations.map(() => false));
    setModalOpen(true);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const ensureAccepted = useCallback(async (): Promise<boolean> => {
    if (state.accepted) return true;
    if (!state.license) {
      // Try to fetch inline if not yet loaded.
      try {
        const res = await elsterApi.license(companyId);
        setState({ accepted: res.accepted, loading: false, license: res.license });
        if (res.accepted) return true;
        return openModal(res.license);
      } catch {
        return false;
      }
    }
    return openModal(state.license);
  }, [state.accepted, state.license, companyId, openModal]);

  async function handleAccept() {
    try {
      await elsterApi.acceptLicense(companyId);
      setState((s) => ({ ...s, accepted: true }));
      setModalOpen(false);
      resolveRef.current?.(true);
      resolveRef.current = null;
    } catch {
      // stay open on error
    }
  }

  function handleCancel() {
    setModalOpen(false);
    resolveRef.current?.(false);
    resolveRef.current = null;
  }

  const license = state.license;
  const allChecked = checks.every(Boolean);

  const Modal: ReactElement | null =
    modalOpen && license ? (
      <LicenseModal
        license={license}
        checks={checks}
        onCheck={(i, v) => setChecks((prev) => prev.map((c, idx) => (idx === i ? v : c)))}
        allChecked={allChecked}
        onAccept={() => { void handleAccept(); }}
        onCancel={handleCancel}
      />
    ) : null;

  return {
    accepted: state.accepted,
    loading: state.loading,
    ensureAccepted,
    Modal,
  };
}

// ---- Modal Component -------------------------------------------------------

interface LicenseModalProps {
  license: ElsterLicense;
  checks: boolean[];
  onCheck: (index: number, value: boolean) => void;
  allChecked: boolean;
  onAccept: () => void;
  onCancel: () => void;
}

function LicenseModal({ license, checks, onCheck, allChecked, onAccept, onCancel }: LicenseModalProps): ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl dark:bg-gray-900">
        {/* Header */}
        <div className="shrink-0 border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">{license.title}</h2>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Bitte lese und akzeptiere die Nutzungsbedingungen, bevor du ELSTER nutzt.
          </p>
        </div>

        {/* Scrollable body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {license.intro && (
            <p className="mb-4 text-sm leading-relaxed text-gray-700 dark:text-gray-300">{license.intro}</p>
          )}

          {license.sections.map((section, si) => (
            <div key={si} className="mb-5">
              {section.heading && (
                <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">{section.heading}</h3>
              )}
              {section.paragraphs.map((p, pi) => (
                <p key={pi} className="mb-2 text-sm leading-relaxed text-gray-700 dark:text-gray-300">{p}</p>
              ))}
              {section.bullets.length > 0 && (
                <ul className="ml-4 list-disc space-y-1">
                  {section.bullets.map((b, bi) => (
                    <li key={bi} className="text-sm text-gray-700 dark:text-gray-300">{b}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {/* Confirmations */}
          {license.confirmations.length > 0 && (
            <div className="mt-4 space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Bestätigungen (alle erforderlich)
              </p>
              {license.confirmations.map((text, i) => (
                <label key={i} className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={checks[i] ?? false}
                    onChange={(e) => onCheck(i, e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>{text}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <Button variant="ghost" onClick={onCancel}>Abbrechen</Button>
          <Button onClick={onAccept} disabled={!allChecked}>Akzeptieren</Button>
        </div>
      </div>
    </div>
  );
}

// ---- Higher-order guard ---------------------------------------------------

/**
 * ElsterLicenseGate wraps children and injects license-gate behaviour.
 * Pass onAccepted to receive a callback that triggers the gate.
 * Children receive no props; the gate is managed via the hook.
 */
export function ElsterLicenseGate({
  companyId,
  children,
}: {
  companyId: string;
  children: ReactNode;
}): ReactElement {
  const { Modal } = useElsterLicense(companyId);
  return (
    <>
      {Modal}
      {children}
    </>
  );
}

// ---- Utility: handle ELSTER_LICENSE_REQUIRED errors ----------------------

/**
 * Returns true if the thrown error is an ELSTER_LICENSE_REQUIRED 403.
 */
export function isLicenseRequired(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403 && err.code === 'ELSTER_LICENSE_REQUIRED';
}
